import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { fetchAllPages } from './fetch-all-pages';
import { useProfile } from './profile';
import type {
  Enrollment,
  EnrollmentProgress,
  EnrollmentListParams,
  EnrollmentStatus,
} from '@/types';

// Raw shape returned by the API (field is `progress`, not `progress_percent`)
type RawEnrollment = Omit<Enrollment, 'progress_percent'> & { progress: number };

export function useMyEnrollments(params?: EnrollmentListParams) {
  const { data: profile } = useProfile();
  return useQuery({
    queryKey: ['enrollments', 'list', params, profile?.id],
    queryFn: () =>
      api.get<RawEnrollment[]>('/enrollments', {
        page: params?.page ? String(params.page) : undefined,
        per_page: params?.per_page ? String(params.per_page) : undefined,
        status: params?.status,
        exclude_status: params?.exclude_status,
        user_id: profile?.id ? String(profile.id) : undefined,
        position_id: params?.position_id ? String(params.position_id) : undefined,
      }),
    select: (res) => ({
      ...res,
      data: res.data.map((e) => ({
        ...e,
        progress_percent: e.progress ?? 0,
      })) as Enrollment[],
    }),
    enabled: !!profile?.id,
    placeholderData: keepPreviousData,
  });
}

// Resolver support: fetch the caller's own enrollments for a given course, so a
// course-level deep-link can redirect to the right enrollment. Passes user_id
// explicitly so it is self-scoped even for admin/super_admin roles (which the
// list endpoint does not auto-scope).
export function useMyEnrollmentsByCourse(courseId: number) {
  const { data: profile } = useProfile();
  return useQuery({
    queryKey: ['enrollments', 'by-course', courseId, profile?.id],
    queryFn: () =>
      fetchAllPages<RawEnrollment>('/enrollments', {
        user_id: profile?.id ? String(profile.id) : undefined,
        course_id: String(courseId),
      }),
    select: (res) =>
      res.data.map((e) => ({ ...e, progress_percent: e.progress ?? 0 })) as Enrollment[],
    enabled: courseId > 0 && !!profile?.id,
  });
}

export function useEnrollmentProgress(enrollmentId: number) {
  return useQuery({
    queryKey: ['enrollments', 'detail', enrollmentId],
    queryFn: () => api.get<EnrollmentProgress>(`/enrollments/${enrollmentId}/progress`),
    select: (res) => res.data,
    enabled: enrollmentId > 0,
  });
}

/**
 * Vuelve a pedir el avance abierto. Las URLs de archivo de las lecciones llegan
 * firmadas (vencen en 1 h) dentro del avance: refrescarlo trae URLs nuevas.
 */
export function useRefreshEnrollmentProgress() {
  const qc = useQueryClient();
  return useCallback(() => qc.invalidateQueries({ queryKey: ['enrollments', 'detail'] }), [qc]);
}

export function useCompleteLesson(enrollmentId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (lessonId: number) =>
      api.post(`/enrollments/${enrollmentId}/lessons/${lessonId}/complete`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['enrollments'] });
      qc.invalidateQueries({ queryKey: ['enrollments', 'detail', enrollmentId] });
    },
  });
}

// --- Instructor/Supervisor: scoped enrollment list ---

export function useScopedEnrollments(params?: {
  page?: number;
  per_page?: number;
  status?: string;
  course_id?: number;
}) {
  return useQuery({
    queryKey: ['enrollments', 'scoped', params],
    queryFn: () =>
      api.get<RawEnrollment[]>('/enrollments', {
        page: params?.page ? String(params.page) : undefined,
        per_page: params?.per_page ? String(params.per_page) : undefined,
        status: params?.status,
        course_id: params?.course_id ? String(params.course_id) : undefined,
      }),
    select: (res) => ({
      ...res,
      data: res.data.map((e) => ({
        ...e,
        progress_percent: e.progress ?? 0,
      })) as Enrollment[],
    }),
    placeholderData: keepPreviousData,
  });
}

// --- Instructor: collaborators eligible for enrollment ---
// Bug #4: the enrollment picker must use this instructor-accessible, company-
// scoped endpoint instead of GET /users (supervisor-only → 403 for instructors).
export type EnrollableUser = {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
};

export function useEnrollableUsers() {
  return useQuery({
    queryKey: ['users', 'enrollable'],
    queryFn: () => api.get<EnrollableUser[]>('/users/enrollable'),
    placeholderData: keepPreviousData,
  });
}

export function useCreateEnrollment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { user_id: number; course_id: number }) => api.post('/enrollments', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['enrollments'] });
    },
  });
}

export function useEnrollByPosition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { position_id: number; course_id: number; user_ids: number[] }) =>
      api.post('/enrollments/by-position', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['enrollments'] });
    },
  });
}

const COUNT_STATUSES: EnrollmentStatus[] = ['active', 'completed', 'expired', 'cancelled'];

export function useEnrollmentCounts(positionId?: number) {
  const { data: profile } = useProfile();
  return useQuery({
    queryKey: ['enrollments', 'counts', profile?.id, positionId],
    queryFn: async () => {
      const results = await Promise.all(
        COUNT_STATUSES.map((s) =>
          api.get<RawEnrollment[]>('/enrollments', {
            user_id: String(profile!.id),
            status: s,
            per_page: '1',
            position_id: positionId ? String(positionId) : undefined,
          }),
        ),
      );
      return Object.fromEntries(
        COUNT_STATUSES.map((s, i) => [s, results[i]?.meta?.pagination?.total ?? 0]),
      ) as Record<EnrollmentStatus, number>;
    },
    enabled: !!profile?.id,
  });
}
